import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Chip, DialogContentText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from "react-toastify";
// Importamos la función de servicio correcta
import {eliminarGastoMedico, obtenerGastosPorTramite} from "../../services/gastoMedicoService";
import { listarMedicos } from "../../services/medicoService";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ModalAgregarGastoMedico from "./ModalAgregarGastoMedico";
import ModalEditarGastoMedico from "./ModalEditarGastoMedico";

// Componente estilizado (reutilizamos el estilo de la tarjeta)
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: 'var(--shadow-md)',
    border: 'none',
    marginBottom: theme.spacing(3),
}));

const ModalGastosPorTramite = ({
                                   open,
                                   onClose,
                                   idTramite, // Solo necesitamos el ID del trámite
                               }) => {
    const [gastosMedicos, setGastosMedicos] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [loadingGastos, setLoadingGastos] = useState(false);

    // Estados para modales y diálogos
    const [modalAgregarAbierto, setModalAgregarAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
    const [dialogoEliminar, setDialogoEliminar] = useState({ open: false, idGastomedico: null });

    // ------------------- LÓGICA DE DATOS -------------------

    // Cargar gastos médicos del trámite
    const cargarGastos = async () => {
        console.log("DEBUG: Intentando cargar gastos para el trámite:", idTramite);

        if (!idTramite) {
            console.log("DEBUG: idTramite no está definido. Cancelando carga de gastos.");
            return;
        }

        setLoadingGastos(true);
        try {
            const gastos = await obtenerGastosPorTramite(idTramite);
            console.log("DEBUG: Gastos recibidos:", gastos);

            if (Array.isArray(gastos)) {
                setGastosMedicos(gastos);
                console.log(`DEBUG: Se cargaron ${gastos.length} gastos médicos.`);
            } else {
                console.error("DEBUG: La respuesta del servicio no fue un array:", gastos);
                setGastosMedicos([]);
                toast.error("Error: Formato de datos de gastos incorrecto.");
            }

        } catch (error) {
            console.error(`Error al cargar gastos del trámite ${idTramite}:`, error);
            toast.error("No se pudieron cargar los gastos del trámite. Verifique la consola para detalles.");
            setGastosMedicos([]);
        } finally {
            setLoadingGastos(false);
        }
    };

    // Cargar datos relacionados (Médicos para mostrar en la tabla)
    const cargarMedicos = async () => {
        try {
            const medicosData = await listarMedicos(true); // Solo activos
            setMedicos(medicosData);
        } catch (error) {
            console.error("Error al cargar:", error);
        }
    };

    // Función para obtener nombre del médico por ID
    const obtenerNombreMedico = (idMedico) => {
        if (!idMedico) return 'No Asignado';
        const medico = medicos.find(m => m.idMedico === idMedico);
        return medico ? `${medico.nombre} ${medico.apellido}` : `ID Médico: ${idMedico}`;
    };

    // Formatear fecha
    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Calcular total de gastos
    const calcularTotalGastos = () => {
        return gastosMedicos.reduce((total, gasto) => total + parseFloat(gasto.monto || 0), 0);
    };

    // Efecto para cargar datos al abrir
    useEffect(() => {
        if (open && idTramite) {
            cargarGastos();
            cargarMedicos();
        }
    }, [open, idTramite]);

    // Función para abrir el modal de agregar gasto
    const abrirModalAgregar = () => {
        setModalAgregarAbierto(true);
    };

    // Función para cerrar el modal de agregar gasto
    const cerrarModalAgregar = () => {
        setModalAgregarAbierto(false);
    };

    // Función para manejar el gasto agregado (se pasará como prop al modal de agregar gasto)
    const handleGastoAgregado = () => {
        cargarGastos();
        toast.success("Gasto agregado exitosamente.");
    };

    // Función para abrir el modal de editar gasto
    const abrirModalEditar = (gasto) => {
        setGastoSeleccionado(gasto.idGastomedico);
        setModalEditarAbierto(true);
    };

    // Función para cerrar el modal de editar gasto
    const cerrarModalEditar = () => {
        setModalEditarAbierto(false);
        setGastoSeleccionado(null);
    };

    // Función para manejar el gasto actualizado (se pasará como prop al modal de editar gasto)
    const handleGastoActualizado = () => {
        cargarGastos();
        toast.success("Gasto actualizado exitosamente.");
    };

    // Función para abrir el diálogo de eliminación
    const abrirDialogoEliminar = (idGasto) => {
        setDialogoEliminar({ open: true, idGastomedico: idGasto });
    };

    // Función para cerrar el diálogo de eliminación
    const cerrarDialogoEliminar = () => {
        setDialogoEliminar({ open: false, idGastomedico: null });
    };

    // Función para confirmar la eliminación del gasto
    const confirmarEliminacion = async () => {
        if (!dialogoEliminar.idGastomedico) return;

        try {
            await eliminarGastoMedico(dialogoEliminar.idGastomedico);
            toast.success("Gasto eliminado exitosamente.");
            setGastosMedicos(prevGastos => prevGastos.filter(gasto => gasto.idGastomedico !== dialogoEliminar.idGastomedico));
            cerrarDialogoEliminar();
        } catch (error) {
            console.error("Error al eliminar gasto:", error);
            toast.error("Error al eliminar el gasto.");
        }
    };

    // ------------------- RENDERIZADO -------------------

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, minHeight: '600px' } }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    py: 3
                }}
            >
                Detalle de Gastos del Trámite
            </DialogTitle>

            <DialogContent sx={{ mt: 2, pb: 1 }}>

                {/* Gastos Médicos del Trámite */}
                <StyledCard>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" color="primary.main">
                                Gastos Registrados
                            </Typography>
                            <button
                                className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-3"
                                onClick={abrirModalAgregar}
                            >
                                <AddCircleOutlineIcon fontSize="small" />
                                Nuevo Gasto
                            </button>
                        </Box>

                        {/* Indicador de carga / Resumen / Tabla */}
                        {loadingGastos ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                                <Typography variant="body1" sx={{ ml: 2 }}>Cargando gastos...</Typography>
                            </Box>
                        ) : gastosMedicos.length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No hay gastos registrados para este trámite.
                            </Alert>
                        ) : (
                            <>
                                {/* Resumen Compacto */}
                                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        <span style={{ color: '#2e7d32' }}>Total Gastado: </span>
                                        <span style={{ color: '#2e7d32' }}>
                                            ${calcularTotalGastos().toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                        <span style={{ margin: '0 50px' }}>|</span>
                                        <span style={{ color: '#1976d2' }}>Total Gastos: </span>
                                        <span style={{ color: '#1976d2' }}>{gastosMedicos.length}</span>
                                        <span style={{ margin: '0 50px'}}>|</span>
                                        <span style={{ color: '#ed6c02' }}>Promedio por Gasto: </span>
                                        <span style={{ color: '#ed6c02' }}>
                                            ${(calcularTotalGastos() / gastosMedicos.length).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </Typography>
                                </Box>

                                {/* Tabla de Gastos */}
                                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Fecha</TableCell>
                                                <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Monto</TableCell>
                                                <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Médico Asignado</TableCell>
                                                <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Comentario</TableCell>
                                                <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {gastosMedicos.map((gasto, idx) => (
                                                <TableRow key={gasto.idGastomedico || idx} hover>
                                                    <TableCell align="center">
                                                        <Chip label={formatearFecha(gasto.fecha)} color="primary" variant="outlined" size="medium" sx={{ fontSize: '14px' }}/>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body1" fontWeight="bold" color="success.main">
                                                            ${parseFloat(gasto.monto).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {obtenerNombreMedico(gasto.idMedico)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body1" sx={{ whiteSpace: 'normal', fontWeight: 'regular' }} title={gasto.comentario}>
                                                            {gasto.comentario || "—"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" onClick={() => abrirModalEditar(gasto)}>Editar</button>
                                                            <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" onClick={() => abrirDialogoEliminar(gasto.idGastomedico)}>Eliminar</button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </StyledCard>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose}>
                    Cerrar
                </button>
            </DialogActions>

            {/* Modales y Diálogos */}
            <ModalAgregarGastoMedico
                open={modalAgregarAbierto}
                onClose={cerrarModalAgregar}
                onGastoAgregado={handleGastoAgregado}
                preSelectedTramite={idTramite}
            />

            <ModalEditarGastoMedico
                open={modalEditarAbierto}
                onClose={cerrarModalEditar}
                gastoId={gastoSeleccionado}
                onGastoActualizado={handleGastoActualizado}
            />

            <Dialog
                open={dialogoEliminar.open}
                onClose={cerrarDialogoEliminar}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" className="fw-bold">
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        ¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button
                        className="btn btn-outline-secondary rounded-pill px-3"
                        onClick={cerrarDialogoEliminar}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-danger rounded-pill px-3 d-flex align-items-center gap-1"
                        onClick={confirmarEliminacion}
                        autoFocus
                    >
                        <DeleteIcon fontSize="small" />
                        Eliminar
                    </button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default ModalGastosPorTramite;
